import { LocationIcon } from '@/components/icons/location-icon';
import { SearchIcon } from '@/components/icons/search-icon';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { PagedListOfTheater } from '@cinemark/content-api-client';
import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import { ZipLocationSearchComponent } from './zip-code-location-search';
import zipCodeLocationStyles from './zip-code-location.module.scss';

type ZipLocationType = 'full' | 'mini-with-dropdown-return-results' | 'mini';

type ZipLocationComponentProps = {
  className?: string;
  miniText1?: string;
  miniText2?: string;
  onMiniClick?: () => void;
  onResults?: (results: PagedListOfTheater) => void;
  type: ZipLocationType;
};

export const ZipLocationComponent = ({ className, miniText1, miniText2, onMiniClick, onResults, type }: ZipLocationComponentProps): React.JSX.Element => {
  const [isZipFormOpen, setIsZipFormOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const locationPanelRef = useRef<HTMLDivElement>(null);
  const searchSvg = <SearchIcon aria-hidden="true"
    className={clsx(
      'icon-lg',
      type !== 'full' ? 'fill-primary group-hover:fill-primary-darker' : 'icon-primary no-hover-effect',
      zipCodeLocationStyles.icon,
      type !== 'full' ? zipCodeLocationStyles.mini : ''
    )}
    viewBox="0 0 20 20"
  />;

  const locationSvg =
    <LocationIcon aria-hidden="true"
      className={clsx('icon-lg icon-primary no-hover-effect', zipCodeLocationStyles.icon, type !== 'full' ? zipCodeLocationStyles.mini : '')}
      viewBox="0 0 20 20"
    />;

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (locationPanelRef.current && !locationPanelRef.current.contains(event.target as Node)) {
        setIsZipFormOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return (): void => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleResults = (results: PagedListOfTheater): void => {
    setIsZipFormOpen(false);
    onResults?.(results);
  };

  if (type === 'mini-with-dropdown-return-results') {
    return (
      <div className="relative flex items-center gap-sm">
        {locationSvg}
        {mounted && miniText1 &&
          <span className="font-bold text-sm"
            data-test="zip-code-search-text"
          >
            {miniText1}
          </span>
        }
        <Button data-test="button-zip-code-search"
          type="button"
          variant="link"
          onClick={(): void => setIsZipFormOpen(true)}
        >
          {miniText2 &&
            <span className="text-primary font-bold text-sm"
              data-test="zip-code-search-text-2"
            >
              {miniText2}
            </span>
          }
        </Button>

        {isZipFormOpen &&
          <Card ref={locationPanelRef}
            className="absolute top-0 right-0"
          >
            <CardContent>
              <ZipLocationSearchComponent type="return-results"
                onResults={handleResults}
              />
            </CardContent>
          </Card>
        }
      </div>
    );
  }

  if (type === 'mini') {
    return (
      <Button className={clsx('flex items-center gap-xs group', className)}
        data-test="button-zip-code-search"
        variant="link"
        onClick={onMiniClick}
      >
        {searchSvg}
        {miniText2 && <span className="text-primary font-bold text-xs"
          data-test="zip-code-search-text-2"
        >
          {miniText2}
        </span>}
      </Button>
    );
  }

  return (
    <div className={clsx('flex flex-col items-center justify-between gap-sm', className)}>
      <div className="flex flex-col items-center gap-sm">
        {searchSvg}
        <p data-test="zip-code-search-description">Find theatres near a ZIP code.</p>
      </div>

      <ZipLocationSearchComponent type={onResults ? 'return-results' : 'search-and-redirect'}
        onResults={handleResults}
      />
    </div>
  );
};
